// Services page: the four business pillars from the company profile, each as a
// detailed ServiceCard (icon + tagline + description + detail list), plus a CTA.
// Content from company-profile/content.md via the `services` namespace. Owned by Agent D.
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DEFAULT_LOCALE } from "../i18n";
import { fadeUp, stagger, revealViewport } from "../motion/variants";
import PageHero from "../components/PageHero";
import ServiceCard, { type ServiceIcon } from "../components/ServiceCard";
import PageMeta from "../components/PageMeta";

const PILLARS: { key: string; icon: ServiceIcon }[] = [
  { key: "container", icon: "container" },
  { key: "vessel", icon: "vessel" },
  { key: "forwarding", icon: "forwarding" },
  { key: "trade", icon: "trade" },
];

export default function Services() {
  const { t } = useTranslation("services");
  const { lang } = useParams();
  const current = lang ?? DEFAULT_LOCALE;

  return (
    <div className="page page-services">
      <PageMeta title={t("meta.title")} description={t("meta.description")} lang={current} />

      <PageHero
        eyebrowEn={t("hero.eyebrowEn")}
        title={t("hero.title")}
        subtitle={t("hero.subtitle")}
      />

      <section className="bg-white py-[var(--space-section)]">
        <div className="mx-auto w-full max-w-6xl px-6">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
            className="grid gap-6 md:grid-cols-2"
          >
            {PILLARS.map(({ key, icon }) => (
              <ServiceCard
                key={key}
                icon={icon}
                title={t(`pillars.${key}.name`)}
                tagline={t(`pillars.${key}.tagline`)}
                desc={t(`pillars.${key}.desc`)}
                points={t(`pillars.${key}.points`, { returnObjects: true }) as string[]}
                variant="detailed"
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-azure)]">
        <div className="mx-auto w-full max-w-4xl px-6 py-[var(--space-section)] text-center">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
          >
            <motion.h2
              variants={fadeUp}
              className="font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-white"
            >
              {t("cta.title")}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-5 max-w-2xl text-[var(--text-lg)] leading-[var(--leading-body)] text-white/90"
            >
              {t("cta.subtitle")}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10">
              <Link
                to={`/${current}/contact`}
                className="inline-flex items-center rounded-[var(--radius-md)] bg-white px-8 py-4 font-medium text-[var(--brand-azure-text)] shadow-[var(--shadow-card)] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("cta.button")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
